import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);
import { Bar } from "react-chartjs-2";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ErrorToast } from '../../components/toastMessage';
import style from "../levelOnePages/levelOneDashboard.module.css";
import { Dialog } from "../../components/CommonComponents/Dialog/Dialog";
import ApplicationsFilter, { ApplicationFilterState } from "../../components/ApplicationsFilter/ApplicationsFilter";
import DataTable, { TableColumn } from "react-data-table-component";
import SortIcon from "../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { formatDate } from "../../utils/date";
import { useToggle } from "../../hooks/common";
import { useNavigate } from "react-router";
import { TApplication, useLevelThreeApplicationCounts, useLevelThreeApplicationStats, useLevelThreeApplications } from "../../hooks/levelThreeApplication.hooks";
import Paginator, {
  PaginatorForRDT,
} from "../../components/CommonComponents/Paginator/Paginator";
import EditButton, { ViewButton } from "../../components/CommonComponents/EditComponent/EditComponent";
import BoxComponent from "../../components/CommonComponents/BoxComponent/BoxComponent";

const statusesApplicableForPaymentScreen = [
  "Pending Payment Level 3 Review",
  "Pending Final Review",
  "Pending Payment Level 2 Review",
  "Completed",

]

const options = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    title: {
      display: true,
      padding: 20,
      font: { size: 24 },
      text: "Applications",
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: "Application Status",
        font: { size: 18 },
        padding: 10,
      },
    },
    y: {
      ticks: { stepSize: 1 },
      title: {
        display: true,
        text: "Number of Applications",
        font: { size: 18 },
        padding: 10,
      },
    },
  },
};

const LevelThreeDashboard = () => {

  const navigate = useNavigate();
  const [filterOpen, toggleFilter] = useToggle();
  const [view, setView] = useState<"chart" | "table">("chart");
  const [page, setPage] = useState(1);
  const [{ sortKey, sortOrder }, setSort] = useState({
    sortKey: "applicationId",
    sortOrder: -1,
  });
  const [filterState, setFilterState] = useState<ApplicationFilterState>({
    connector: "and",
    conditions: [],
  });
  const { data } = useLevelThreeApplicationStats({ filters: filterState });
  const labels = (data?.data || []).map((e) => e.status);

  const { isLoading: isLoading1, data: counts } = useLevelThreeApplicationCounts({
    filters: filterState,
  });
  const { isLoading: isLoading2, data: listData } = useLevelThreeApplications({
    page,
    limit: 10,
    sort: sortKey,
    sortVal: sortOrder,
    filters: filterState,
  });

  const chartData: ChartData<"bar", number[], string> = {
    labels,
    datasets: [
      {
        data: (data?.data || []).map((e) => e.count),
        barThickness: 45,
        backgroundColor: "rgba(53, 162, 235,1)",
      },
    ],
  };

  const sortedData = useMemo(() => {
    if (!listData?.data) return [];

    return [...listData.data].sort((a, b) => {
      if (a.isActionAllowed && !b.isActionAllowed) return -1;
      if (!a.isActionAllowed && b.isActionAllowed) return 1;
      return 0;
    });
  }, [listData]);

  const columns = useMemo(
    (): TableColumn<TApplication>[] => [
      {
        name: "Application ID",
        sortable: true,
        width: "180px",
        sortField: "applicationId",
        selector: (d) => d.applicationId,
        center: true,
        cell: (e) => {
          const disabled = !e.isActionAllowed;

          return (
              <span
                  className={`font-semibold ${
                      disabled
                          ? "text-gray-400 cursor-not-allowed"
                          : "text-[#E9B925] cursor-pointer underline"
                  }`}
                  onClick={() => {
                    if (disabled) return;

                    if (statusesApplicableForPaymentScreen.includes(e.status)) {
                      navigate(`/level-3-payment-details`, { state: { id: e.id } });
                    } else {
                      navigate(`/level-three-application-detail`, { state: { id: e.id } });
                    }
                  }}
              >
        {e.applicationId}
      </span>
          );
        },
      },

      {
        name: "Plot Owner's Name",
        sortable: true,
        sortField: "plot_owner_s_name",
        selector: (d) => d.plot_owner_s_name || "-",
        cell: (d) => (
          <span className="text-center">{d.plot_owner_s_name || "-"}</span>
        ),
        center: true,
        grow: 2,
      },
      {
        name: "Status",
        width: '230px',
        center: true,
        sortable: true,
        sortField: "status",
        cell: (d) => <div className="text-center">{d.status}</div>,
      },
      {
        name: "Date of Submission",
        width: '230px',
        center: true,
        sortable: true,
        sortField: "submittedAt",
        cell: (d) => (d.submittedAt ? formatDate(d.submittedAt) : "-"),
      },
      {
        name: 'Created By',
        selector: (row: any) => row.createdBy,
        cell: (row: any) => <span className="text-center">{row.createdBy}</span>,
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Created On',
        selector: (row: any) => formatDate(new Date(row.createdAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified By',
        selector: (row: any) => row.modifiedBy,
        cell: (row: any) => <span className="text-center">{row.modifiedBy}</span>,
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified On',
        selector: (row: any) => formatDate(new Date(row.updatedAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: "Action",
        width: '140px',
        center: true,
        cell: (e) => {
          const disabled = !e.isActionAllowed;

          return (
              <ViewButton
                  disabled={disabled}
                  onClick={() => {
                    if (disabled) return;

                    if (statusesApplicableForPaymentScreen.includes(e.status)) {
                      navigate(`/level-3-payment-details`, { state: { id: e.id } });
                    } else {
                      navigate(`/level-three-application-detail`, { state: { id: e.id } });
                    }
                  }}
              />
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <>
      <div className="p-4">
        <h4 className="text-xl sm:text-2xl font-bold pb-4">Dashboard</h4>
        <BoxComponent>
          <div className="p-2 sm:p-4 md:p-4 md:pt-2 bg-white">
            <div className="flex justify-end mb-4">
              {/* <h4 className="text-xl sm:text-2xl font-bold">Dashboard</h4> */}
              <div className="flex space-x-4">
                <button
                  className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
                  onClick={() => {
                    setView((e) => (e === "chart" ? "table" : "chart"));
                    setPage(1);
                  }}
                >
                  {view === "table" ? (
                    <i className="fa fa-bar-chart text-primary m-0" />
                  ) : (
                    <i className="fa fa-list-ul text-primary m-0" />
                  )}
                </button>
                <button
                  className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center min-w-[42px]"
                  onClick={toggleFilter}
                >
                  <i className="fa fa-filter text-primary m-0" />
                </button>
              </div>
            </div>
            {view === "chart" ? (
              <div className="chart-container overflow-x-auto" style={{ position: 'relative', height: '75vh', width: '100%' }}>
                <div style={{ minWidth: '900px', height: '100%' }}>
                  <Bar options={options} data={chartData} />
                </div>
              </div>
            ) : (
              <DataTable
                pagination
                sortServer
                paginationServer
                className="mt-4"
                columns={columns}
                sortIcon={<SortIcon />}
                 data={sortedData || []}
                customStyles={tableCustomStyles}
                onChangePage={(p) => setPage(p)}
                paginationTotalRows={counts?.data || 0}
                progressPending={isLoading1 || isLoading2}
                paginationComponent={PaginatorForRDT}
                onSort={(col, dir) => {
                  if (!col?.sortField) return;
                  setSort({
                    sortKey: col.sortField || "",
                    sortOrder: dir === "asc" ? 1 : -1,
                  });
                }}
              />
            )}
          </div>
        </BoxComponent>
      </div>
      <Dialog
        onClose={toggleFilter}
        open={filterOpen}
        className="flex flex-col sm:w-[600px] lg:w-[800px]"
      >
        <ApplicationsFilter
          onClose={toggleFilter}
          defaultFilter={filterState}
          onChange={setFilterState}
        />
      </Dialog>
    </>
  );
};

export default LevelThreeDashboard;
